/* eslint-disable jsx-a11y/role-supports-aria-props */
/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import { checkIsActive } from "../../../../_helpers";
import AuthService from "../../../../../app/base/services/authentication.service";
import { LogoutIcon } from "../../../../../app/assets/icons";
import { useTranslation } from "../../../../../app/i18n/LanguageContext";
import { requestStore } from "../../../../../app/pages/admin/request/components/store/requestStore";
import { expertsRequestStore } from "../../../../../app/pages/admin/team/store/expertsRequestStore";
import { useNotificationStore } from "../../subheader/components/useNotificationStore";
import { UserNotificationsEnum } from "../../../../../app/pages/auth/userNotifications.enum";
import { UserRolesEnum } from "../../../../../app/pages/auth/userRoles.enum";
import "./custom.scss";

// Notification types that mean "a request landed/changed in one of the
// aside-menu tabs below" - used to keep the pending-count badges live
// without a dedicated polling mechanism.
const REQUEST_TAB_NOTIFICATION_TYPES = {
   [UserNotificationsEnum.AdminRegistration]: "requests",
   [UserNotificationsEnum.AdminWorkspaceRequest]: "requests",
   [UserNotificationsEnum.AdminAssessmentRequest]: "requests",
   [UserNotificationsEnum.AdminExpertRequest]: "experts"
};

// Maps each sidebar section's stable `id` (from Menu.js) to its translation
// key, so section headers ("Stakeholders", "Setup", ...) can be translated
// without touching Menu.js's plain data. Sections not listed here just fall
// back to their untranslated title.
const SECTION_TITLE_KEYS = {
   section_people: "menu-section-stakeholders",
   section_assessment_mgmt: "menu-section-setup",
   section_implementation: "menu-section-implementation",
   section_organization_management: "menu-section-organization-management",
   section_my_account: "menu-section-my-account",
   section_support: "menu-section-support"
};

export function AdminAsideMenuList({ data, layoutProps }) {
   const [activeMenuItem, setActiveMenuItem] = useState(null);
   const location = useLocation();
   const navigate = useNavigate();
   const { t } = useTranslation();

   const requestStatistics = requestStore((state) => state.requestStatistics);
   const setRequestStatistics = requestStore((state) => state.setRequestStatistics);
   const expertsPendingCount = expertsRequestStore((state) => state.pendingCount);
   const fetchExpertsPendingCount = expertsRequestStore((state) => state.fetchPendingCount);
   const newNotification = useNotificationStore((state) => state.newNotification);

   const isSuperAdmin = AuthService.isAuthorized([UserRolesEnum.SuperAdmin]);

   const pendingBadgeCounts = {
      requests:
         (requestStatistics?.organizations?.Pending || 0) +
         (requestStatistics?.workspaces?.Pending || 0) +
         (requestStatistics?.assessments?.Pending || 0),
      experts: expertsPendingCount || 0
   };

   useEffect(() => {
      if (!isSuperAdmin) return;
      setRequestStatistics();
      fetchExpertsPendingCount();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isSuperAdmin]);

   useEffect(() => {
      if (!newNotification) return;
      const affectedTab = REQUEST_TAB_NOTIFICATION_TYPES[newNotification?.notificationType];
      if (affectedTab === "requests") setRequestStatistics();
      if (affectedTab === "experts") fetchExpertsPendingCount();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [newNotification]);

   const handleMenuClick = (itemId) => {
      setActiveMenuItem(itemId);
   };

   useEffect(() => {
      const currentRoute = location.pathname;
      const isDetailsTicketActive = currentRoute.includes("/admin/help-desk/details/");
      const matchingMenuItem = data.find((menuItem) => currentRoute.startsWith(menuItem.url));

      if (isDetailsTicketActive) {
         setActiveMenuItem("admin_page_ticket");
      } else if (matchingMenuItem) {
         setActiveMenuItem(matchingMenuItem.id);
      }
   }, [location.pathname]);

   const getMenuItemActive = (url, hasSubmenu = false, id) => {
      return checkIsActive(location, url) ||
         (id === "admin_page_ticket" && location?.pathname?.includes("/admin/help-desk/"))
         ? ` ${!hasSubmenu && "menu-item-active"} menu-item-open menu-item-not-hightlighted`
         : "";
   };

   const logout = () => {
      AuthService.logout();
      navigate("/auth/login");
   };

   const renderPlainMenuItem = (menuItem) =>
      AuthService.isAuthorized(menuItem.role) && (
         <li
            key={menuItem.id}
            className={`menu-item ${menuItem.subMenus?.length > 0 &&
               "menu-item-submenu "} ${getMenuItemActive(
               menuItem.url,
               menuItem.children?.length > 0 ? true : false,
               menuItem.id
            )}`}
            aria-haspopup="true"
         >
            <NavLink
               className={`menu-link ${menuItem.isDisabled &&
                  " disabled"} ${menuItem.subMenus?.length > 0 && "menu-toggle"}`}
               to={menuItem.url}
               onClick={() => handleMenuClick(menuItem.id)}
            >
               <span
                  className="menu-icon"
                  style={{
                     WebkitMaskImage: `url(${menuItem.icons.active})`,
                     maskImage: `url(${menuItem.icons.active})`
                  }}
               />
               <span className="menu-text">{t(`menu-item-${menuItem.id}`)}</span>
               {menuItem.subMenus?.length > 0 && <i className="menu-arrow" />}
            </NavLink>

            <div className="menu-submenu ">
               <i className="menu-arrow" />
               <ul className="menu-subnav">
                  {/*begin::2 Level*/}
                  {menuItem.subMenus?.length > 0 &&
                     menuItem.subMenus.map((subMenu) => {
                        return (
                           <li
                              key={subMenu.id}
                              className={`menu-item menu-item-submenu ${getMenuItemActive(
                                 subMenu.url,
                                 subMenu.children?.length > 0 ? true : false,
                                 menuItem.id
                              )}`}
                              aria-haspopup="true"
                              data-menu-toggle="hover"
                           >
                              <NavLink
                                 className={`menu-link menu-toggle ${menuItem.isDisabled &&
                                    " disabled"}`}
                                 to={subMenu.url}
                              >
                                 <i className="menu-bullet menu-bullet-dot">
                                    <span />
                                 </i>
                                 <span className="menu-text">{subMenu.title}</span>
                                 {subMenu.children?.length > 0 && <i className="menu-arrow" />}
                              </NavLink>
                              {subMenu.children?.length > 0 && (
                                 <div className="menu-submenu ">
                                    <i className="menu-arrow" />
                                    <ul className="menu-subnav">
                                       {/*begin::3 Level*/}
                                       {subMenu.children?.map((childItem) => {
                                          return (
                                             <li
                                                key={childItem.id}
                                                className={`menu-item  ${getMenuItemActive(
                                                   childItem.url
                                                )}`}
                                                aria-haspopup="true"
                                             >
                                                <NavLink
                                                   className={`menu-link ${childItem.isDisabled &&
                                                      " disabled"}`}
                                                   to={childItem.url}
                                                >
                                                   <i className="menu-bullet menu-bullet-dot">
                                                      <span />
                                                   </i>
                                                   <span className="menu-text">
                                                      {childItem.title}
                                                   </span>
                                                </NavLink>
                                             </li>
                                          );
                                       })}
                                    </ul>
                                 </div>
                              )}
                           </li>
                        );
                     })}
                  {/*end::2 Level*/}
               </ul>
            </div>
         </li>
      );

   // Items flagged pinBottom (e.g. "Help desk") render in their own list,
   // pinned to the bottom of the sidebar via CSS instead of scrolling away
   // with the rest of the nav.
   const mainMenuData = data.filter((menuItem) => !menuItem.pinBottom);
   const pinnedMenuData = data.filter((menuItem) => menuItem.pinBottom);

   return (
      <>
         {/* begin::Menu Nav */}
         <ul className={`menu-nav ${layoutProps.ulClasses}`}>
            {/*begin::1 Level*/}
            {mainMenuData.length !== 0
               ? mainMenuData.map((menuItem) => {
                    if (menuItem.type === "section" && menuItem.children) {
                       const items = menuItem.children || [];
                       const visibleItems = items.filter((child) =>
                          AuthService.isAuthorized(child.role)
                       );

                       // Skip the section entirely when the current role can't see any of
                       // its items, instead of leaving a bare heading with nothing under it.
                       if (visibleItems.length === 0) return null;

                       return (
                          <React.Fragment key={menuItem.id}>
                             <li className="menu-section menu-section-edit">
                                <h4 className="menu-text">
                                   {t(SECTION_TITLE_KEYS[menuItem.id] || menuItem.title)}
                                </h4>
                             </li>

                             {visibleItems.map((child) => (
                                <li
                                   key={child.id}
                                   className={`menu-item ${getMenuItemActive(child.url)}`}
                                >
                                   <NavLink
                                      className="menu-link"
                                      to={child.url}
                                      onClick={() => handleMenuClick(child.id)}
                                   >
                                      <span
                                         className="menu-icon"
                                         style={{
                                            WebkitMaskImage: `url(${child.icons.active})`,
                                            maskImage: `url(${child.icons.active})`
                                         }}
                                      />
                                      <span className="menu-text">
                                         {t(`menu-item-${child.id}`)}
                                      </span>
                                      {pendingBadgeCounts[child.id] > 0 && (
                                         <span className="menu-badge">
                                            {pendingBadgeCounts[child.id]}
                                         </span>
                                      )}
                                   </NavLink>
                                </li>
                             ))}
                          </React.Fragment>
                       );
                    }

                    if (menuItem.type === "divider") {
                       return (
                          AuthService.isAuthorized(menuItem.role) && (
                             <li key={menuItem.id} className="menu-section ">
                                <h4 className="menu-text">{menuItem.title}</h4>
                                <i className="menu-icon flaticon-more-v2"></i>
                             </li>
                          )
                       );
                    } else {
                       return renderPlainMenuItem(menuItem);
                    }
                 })
               : null}

            {/*end::1 Level*/}

            {/* Components */}
            {/* begin::section */}

            {/* end:: section */}
         </ul>
         {/* end::Menu Nav */}

         {pinnedMenuData.length > 0 && (
            <ul className={`menu-nav menu-nav-pinned ${layoutProps.ulClasses}`}>
               {pinnedMenuData.map((menuItem) => renderPlainMenuItem(menuItem))}
            </ul>
         )}
      </>
   );
}
